/**
 * Emit host for the dictaphone device-setup surfaces. Bound into
 * `@heidi/dictate`'s analytics-ports seam by `dictation-analytics-wiring`.
 * This file owns the whole taxonomy for these surfaces — screen names, button
 * actions and routing — so `/ui` reports behaviour and nothing else.
 *
 * TWO ROUTING FACTS, both easy to get wrong and both load-bearing:
 *
 * 1. `screen_viewed` is on the pipeline's Snowplow denylist
 *    (`snowplow-suppression.ts`) because ~2.8M/7d of it had no warehouse
 *    consumer. A new impression event therefore needs an EXPLICIT
 *    `snowplow: true` or it lands Datadog-only — no Snowplow, and no
 *    Amplitude either once `amplitude: false` is set. That combination looks
 *    like working instrumentation and produces nothing queryable, which is
 *    exactly what this surface had before. The denylist's own docblock
 *    sanctions the opt-out for a reviewed call site.
 * 2. `amplitude: false` on every emit: these are new events, and the routing
 *    policy blocks new events from Amplitude. Amplitude defaults ON in the
 *    dispatcher, so omitting `options` opts back in silently.
 *
 * ONLY SCHEMA-BACKED PROPS SHIP. `screen_viewed` carries `screen_name` and
 * `entry_point`; it has no `version` field, and `button_clicked` has none
 * either. An unknown prop either fails Iglu validation outright
 * (`additionalProperties: false`) or arrives with no warehouse column — so the
 * card's phase rides its screen name instead. Adding a real `version`/`model`
 * dimension means an ADDITION bump in `oscerai/snowplow` first; see the
 * `trackMapFunctionalityViewed` note.
 */
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type DictateDeviceSetupCardOutcome,
  type DictateDeviceSetupCardPhase,
  type DictationDeviceSetupAnalyticsPorts,
} from '@heidi/dictate/contracts';
import { type MappedAction } from '@heidi/dictate/data';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * The mapped actions a forwarded press can dispatch. Derived from the mapping
 * vocabulary rather than restated so a new action cannot ship untracked — it
 * breaks the exhaustive `BUTTON_PRESS_ACTIONS` map below instead.
 */
type DictateDeviceButtonAction = Exclude<MappedAction['type'], 'start_stop_dictating'>;

/** Snowplow-first: impressions here are opt-ins past the `screen_viewed` denylist. */
const IMPRESSION_OPTIONS = { amplitude: false, snowplow: true } as const;

/** The phase IS the screen, because the schema has nowhere else to put it. */
const CARD_SCREEN_BY_PHASE: Record<DictateDeviceSetupCardPhase, ScreenName> = {
  detected: ScreenName.DictateDeviceSetupCard,
  setup: ScreenName.DictateDeviceSetupCardV2,
};

const CARD_OUTCOME_ACTIONS: Record<DictateDeviceSetupCardOutcome, ButtonAction> = {
  set_up_now: ButtonAction.DictateDeviceSetupCardSetUpNow,
  later: ButtonAction.DictateDeviceSetupCardLater,
  got_it: ButtonAction.DictateDeviceSetupCardGotIt,
  close: ButtonAction.DictateDeviceSetupCardClose,
};

export const dictateDeviceSetupAnalyticsPorts: DictationDeviceSetupAnalyticsPorts = {
  trackCardViewed: (phase) => {
    track({
      event_name: TrackingEventName.ScreenViewed,
      props: { screen_name: CARD_SCREEN_BY_PHASE[phase] },
      options: IMPRESSION_OPTIONS,
    });
  },

  // `button_clicked` is NOT denylisted, and `trackButtonClicked`'s own helper
  // already pins `amplitude: false`, so this reaches Snowplow + Datadog the
  // same way every other reviewed button does. Spelled out here rather than
  // delegated so the action map and the screen name stay in one place.
  trackCardOutcome: (phase, outcome) => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: CARD_SCREEN_BY_PHASE[phase],
        action: CARD_OUTCOME_ACTIONS[outcome],
        label: outcome,
      },
      options: { amplitude: false },
    });
  },

  trackAddDeviceViewed: (entryPoint) => {
    track({
      event_name: TrackingEventName.ScreenViewed,
      props: {
        screen_name: ScreenName.DictateDeviceSetupAddDevice,
        // Schema-backed, and the dimension the rollout actually needs: did the
        // card drive this setup, or did the clinician find it in Settings?
        entry_point: entryPoint,
      },
      options: IMPRESSION_OPTIONS,
    });
  },

  // Deliberately carries no device model. The model would be the natural
  // second dimension here ("which hardware do people actually configure?"),
  // but `screen_viewed` has no field for it and the device KEY must never be
  // sent — it embeds the USB serial. Adding `device_model` to the Iglu schema
  // (ADDITION bump, then a Snowtype regen) is the follow-up; shipping it as an
  // unknown prop now would put the whole event in the bad-events stream.
  trackMapFunctionalityViewed: (entryPoint) => {
    track({
      event_name: TrackingEventName.ScreenViewed,
      props: {
        screen_name: ScreenName.DictateDeviceSetupMapFunctionality,
        // Not redundant with the wizard's entry point: Settings reaches this
        // dialog directly whenever the target device is unambiguous, so for
        // the common Settings path there is no wizard impression to join to.
        entry_point: entryPoint,
      },
      options: IMPRESSION_OPTIONS,
    });
  },

  trackMapFunctionalitySaved: (entryPoint) => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.DictateDeviceSetupMapFunctionality,
        action: ButtonAction.DictateDeviceSetupMapSave,
        label: 'save_setup',
        // Pairs with the impression above so completion reads per surface.
        entry_point: entryPoint,
      },
      options: { amplitude: false },
    });
  },
};

const BUTTON_PRESS_ACTIONS: Record<DictateDeviceButtonAction, ButtonAction> = {
  next_field: ButtonAction.DictateDeviceButtonNextField,
  previous_field: ButtonAction.DictateDeviceButtonPreviousField,
  insert_snippet: ButtonAction.DictateDeviceButtonInsertSnippet,
};

/**
 * A mapped dictaphone button actually dispatching its action. Pairs with
 * `trackMapFunctionalitySaved` to answer the question the save alone cannot:
 * how many clinicians who configured a button go on to use it.
 *
 * NOT on the `deviceSetup` port. The seam exists so `/ui` can emit without
 * importing app analytics; the only callers here are the app-cone press
 * listeners, which already own the Tauri payload type.
 *
 * NO SCREEN NAME, deliberately — a hardware press has no surface, and
 * `screen_name` is optional on `button_clicked`. `click_source` carries the
 * physical `buttonId` (`record`, `f1`, …), which is reader vocabulary bounded
 * by device model. The device KEY is never sent: it embeds the USB serial.
 *
 * `start_stop_dictating` has no member. Rust handles that button natively and
 * omits `action` when it forwards the press, so on this event it is
 * indistinguishable from an unmapped button — tracking it here would report
 * dead presses as dictation. It reports on the session funnel's `triggered_by`
 * as `dictaphone_hold` / `dictaphone_hands_free` instead.
 */
export const trackDictateDeviceButtonPressed = (
  action: DictateDeviceButtonAction,
  buttonId: string
): void => {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      action: BUTTON_PRESS_ACTIONS[action],
      label: action,
      click_source: buttonId,
    },
    options: { amplitude: false },
  });
};
